import React, { useState } from 'react'

const INITIAL_VISIBLE_COUNT = 8

// A single dimension's facet controls: collapsible header, sorted checkbox
// list, and a "show more" toggle when the dimension has >8 values.
//
// Props:
//   dimension       — { name, slug }
//   values          — [{ id, name, slug, count }, ...]
//   selectedSlugs   — array of slugs currently selected in the store
//   onToggle(slug)  — called when a checkbox is toggled
export default function FacetGroup({ dimension, values, selectedSlugs = [], onToggle }) {
  const [expanded, setExpanded] = useState(true)
  const [showAll, setShowAll]   = useState(false)

  const sorted = [ ...(values || []) ].sort((a, b) => {
    const byCount = (b.count || 0) - (a.count || 0)
    if (byCount !== 0) return byCount
    return (a.name || '').localeCompare(b.name || '')
  })

  const visible = showAll ? sorted : sorted.slice(0, INITIAL_VISIBLE_COUNT)
  const remaining = sorted.length - visible.length

  return (
    <div className="border-b border-slate-100 py-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between text-left"
        aria-expanded={expanded}
      >
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {dimension.name}
        </span>
        <svg
          className={`h-3 w-3 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="mt-2 space-y-1">
          {sorted.length === 0 && (
            <p className="text-xs text-slate-400 italic py-1">No values yet</p>
          )}

          {visible.map((value) => {
            const checked  = selectedSlugs.includes(value.slug)
            const isEmpty  = (value.count || 0) === 0
            return (
              <label
                key={value.id || value.slug}
                className={`flex items-center gap-2 rounded-md px-2 py-1 text-sm transition ${
                  isEmpty ? 'text-slate-400' : 'text-slate-700 hover:bg-slate-50 cursor-pointer'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(value.slug)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-accent-600 focus:ring-accent-500"
                />
                <span className="flex-1 truncate">{value.name}</span>
                <span className="text-xs text-slate-400">{value.count ?? 0}</span>
              </label>
            )
          })}

          {remaining > 0 && !showAll && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="mt-1 text-xs font-medium text-accent-600 hover:text-accent-500"
            >
              Show {remaining} more
            </button>
          )}
          {showAll && sorted.length > INITIAL_VISIBLE_COUNT && (
            <button
              type="button"
              onClick={() => setShowAll(false)}
              className="mt-1 text-xs font-medium text-slate-500 hover:text-slate-700"
            >
              Show less
            </button>
          )}
        </div>
      )}
    </div>
  )
}
